import React, { Suspense, useCallback,useState } from "react"
import { graphql, useLazyLoadQuery, useMutation } from "react-relay"
import { cn } from "src/common/utils"
import { ScopeGate } from "src/components/ScopeGate"
import { useMe } from "src/context/MeContext"
import { useToast } from "src/hooks/use-toast"
import { getProductName } from "src/utils/brandTheme"

import type { TeamSelectionModalCreateTeamMutation } from "./__generated__/TeamSelectionModalCreateTeamMutation.graphql"
import type { TeamSelectionModalQuery } from "./__generated__/TeamSelectionModalQuery.graphql"
import { Button } from "./ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { LoadingSpinner } from "./ui/loading-spinner"

const teamSelectionQuery = graphql`
  query TeamSelectionModalQuery($organizationId: GlobalID!) {
    node(id: $organizationId) {
      ... on GraphQLOrganization {
        teams {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
  }
`

const createTeamMutation = graphql`
  mutation TeamSelectionModalCreateTeamMutation($input: CreateTeamInput!) {
    createTeam(input: $input) {
      team {
        id
        name
      }
    }
  }
`

function TeamList({
  organizationId,
  onSelectTeam,
  onCreateTeam
}: {
  organizationId: string,
  onSelectTeam: (id: string, name: string, isNewTeam?: boolean) => void | Promise<void>,
  onCreateTeam: (isFirstTeam: boolean) => void
}) {
  // Query teams for the organization
  const data = useLazyLoadQuery<TeamSelectionModalQuery>(
    teamSelectionQuery,
    { organizationId }
  )

  // Extract teams from query response
  const teamsUnsorted = data.node?.teams?.edges || []

  // Sort teams alphabetically by name
  const teams = [...teamsUnsorted].sort((a, b) =>
    a.node.name.localeCompare(b.node.name)
  )

  // Auto-select if there's only one team, or show create dialog if no teams
  const [hasRequestedFirstTeam, setHasRequestedFirstTeam] = React.useState(false)

  React.useEffect(() => {
    if (teams.length === 1) {
      const team = teams[0]
      onSelectTeam(team.node.id, team.node.name, false)
    } else if (teams.length === 0 && !hasRequestedFirstTeam) {
      // No teams exist, show create dialog for first-time setup
      setHasRequestedFirstTeam(true)
      onCreateTeam(true)
    }
  }, [teams, onSelectTeam, onCreateTeam, hasRequestedFirstTeam])

  // If no teams, show message while transitioning to create dialog
  if (teams.length === 0) {
    return (
      <div className="p-4 text-muted-foreground text-center">
        <LoadingSpinner className="inline-block mr-2 h-4 w-4 text-primary" />
        Setting up your organization...
      </div>
    )
  }

  if (teams.length === 1) {
    return (
      <div className="p-4 text-muted-foreground text-center">
        <LoadingSpinner className="inline-block mr-2 h-4 w-4 text-primary" />
        Auto-selecting the only available team: {teams[0].node.name}
      </div>
    )
  }

  return (
    <div className="grid gap-2 p-2">
      {teams.map((edge) => (
        <button
          key={edge.node.id}
          className={cn(
            "px-3 py-2 rounded cursor-pointer hover:bg-accent text-left",
            "transition-colors duration-150 text-foreground border border-border"
          )}
          onClick={() => onSelectTeam(edge.node.id, edge.node.name, false)}
        >
          {edge.node.name}
        </button>
      ))}
      <ScopeGate scope="write:teams">
        <Button
          onClick={async (e) => {
            e.preventDefault()
            // Select the first team to dismiss the modal, then redirect to create team page
            if (teams.length > 0) {
              const firstTeam = teams[0]
              await onSelectTeam(firstTeam.node.id, firstTeam.node.name, false)
            }
            window.location.href = "/settings/teams/new"
          }}
          variant="outline"
          size="sm"
          className="w-full mt-0.5"
          type="button"
        >
          Create New Team
        </Button>
      </ScopeGate>
    </div>
  )
}

function CreateTeamDialog({
  onCreateTeam,
  onBack,
  isOpen,
  isFirstTeam = false
}: {
  onCreateTeam: (id: string, name: string) => void | Promise<void>
  onBack: () => void
  isOpen: boolean
  isFirstTeam?: boolean
}) {
  const me = useMe();
  const productName = getProductName(me.organization?.brandTheme);
  const [teamName, setTeamName] = useState("")
  const { toast } = useToast()
  const [commitCreateTeam, isCreating] = useMutation<TeamSelectionModalCreateTeamMutation>(createTeamMutation)

  const handleCreate = () => {
    if (!teamName.trim()) {
      toast({
        title: "Error",
        description: "Team name is required",
        variant: "error",
      })
      return
    }

    commitCreateTeam({
      variables: {
        input: {
          name: teamName.trim(),
        },
      },
      onCompleted: (response) => {
        if (response.createTeam?.team) {
          // Call onCreateTeam which will handle team selection and redirect
          // No need to reset form - redirect will happen
          onCreateTeam(response.createTeam.team.id, response.createTeam.team.name)
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "error",
        })
      },
    })
  }

  // For first team setup, hide the close icon but still allow controlled closing via onBack
  // Don't allow closing during an in-flight mutation
  const handleOpenChange = () => {
    if (!isCreating) {
      onBack()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent hideCloseButton={isFirstTeam}>
        <DialogHeader>
          <DialogTitle>
            {isFirstTeam ? "Create Your First Team" : "Create Team"}
          </DialogTitle>
          <DialogDescription>
            {isFirstTeam
              ? `Welcome! Create a team to get started with ${productName}.`
              : "Create a new team in your organization."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="team-name">Team Name</Label>
            <Input
              id="team-name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter team name"
              className="mt-2"
            />
          </div>
        </div>
        <DialogFooter>
          {!isFirstTeam && (
            <Button
              variant="outline"
              onClick={onBack}
              disabled={isCreating}
            >
              Back to Team Selection
            </Button>
          )}
          <Button
            onClick={handleCreate}
            disabled={isCreating || !teamName.trim()}
          >
            {isCreating ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface TeamSelectionModalProps {
  organizationId: string
  onSelectTeam: (id: string, name: string, isNewTeam?: boolean) => void | Promise<void>
  isOpen: boolean
}

export function TeamSelectionModal({
  organizationId,
  onSelectTeam,
  isOpen
}: TeamSelectionModalProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [isFirstTeam, setIsFirstTeam] = useState(false)

  const handleCreateTeam = useCallback(async (teamId: string, teamName: string) => {
    // Auto-select the newly created team
    // Don't close the dialog - the redirect/reload will unmount everything
    await onSelectTeam(teamId, teamName, true)
  }, [onSelectTeam])

  const handleShowCreateDialog = useCallback((firstTeam: boolean = false) => {
    setIsFirstTeam(firstTeam)
    setShowCreateDialog(true)
  }, [])

  const handleBackToSelection = useCallback(() => {
    setShowCreateDialog(false)
    setIsFirstTeam(false)
  }, [])

  // Don't render anything if not open (must be after all hooks)
  if (!isOpen || !organizationId) return null

  return (
    <>
      <CreateTeamDialog
        onCreateTeam={handleCreateTeam}
        onBack={handleBackToSelection}
        isOpen={showCreateDialog}
        isFirstTeam={isFirstTeam}
      />

      {!showCreateDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="w-full max-w-md bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
            <div className="border-b border-border p-4">
              <h2 className="text-xl font-semibold text-foreground">Select a Team</h2>
              <p className="text-muted-foreground mt-1">
                Please select a team or create a new team if your team does not exist to filter what you see.
              </p>
            </div>

            <div className="max-h-[50vh] overflow-y-auto">
              <Suspense fallback={
                <div className="flex items-center justify-center p-8">
                  <LoadingSpinner className="h-8 w-8 text-primary" />
                  <span className="ml-3 text-muted-foreground">Loading teams...</span>
                </div>
              }>
                <TeamList
                  organizationId={organizationId}
                  onSelectTeam={onSelectTeam}
                  onCreateTeam={handleShowCreateDialog}
                />
              </Suspense>
            </div>
          </div>
        </div>
      )}
    </>
  )
}